<template>
  <VCard>
    <VToolbar>
      <VToolbarTitle>
        Edit Contact
      </VToolbarTitle>
      <VToolbarTitle class="sub-title">
        ({{ page }} / {{ totalCount }})
      </VToolbarTitle>
      <VSpacer />
      <VBtn
        small
        flat
        icon
        @click.prevent="onSaveContact"
      >
        <VIcon>
          save
        </VIcon>
      </VBtn>
      <VBtn
        small
        flat
        icon
        @click.prevent="onMessageClicked"
      >
        <VIcon>
          message
        </VIcon>
      </VBtn>
    </VToolbar>
    <VLayout row>
      <VFlex
        xs12
        align-center
      >
        <VSlider
          v-model="page"
          :max="totalCount"
          min="1"
          prepend-icon="first_page"
          append-icon="last_page"
          @start="onSliderStarted"
          @change="onSliderChanged"
        />
      </VFlex>
    </VLayout>
    <VLayout row>
      <VFlex
        xs5
        sm4
        md4
        lg4
        align-center
        justify-center
        layout
        text-xs-right
      >
        <AvatarUploader
          v-if="avatarImg"
          ref="avatarUploader"
          :avatar-img="avatarImg"
          :contact-id="contactId"
          :contact-index="contactIndex"
          :avatar-placeholder="avatarPlaceholder"
        />
      </VFlex>
      <VFlex
        xs7
        sm8
        md8
        lg8
      >
        <VCardTitle primary-title>
          <div>
            <div class="headline">
              {{ firstName }} {{ lastName }}
            </div>
            <div> {{ number }}</div>
          </div>
        </VCardTitle>
      </VFlex>
    </VLayout>
    <VContainer
      grid-list-sm
      class="pa-4"
    >
      <VLayout
        row
        wrap
      >
        <VFlex
          xs4
          sm2
        >
          <VLayout
            align-center
            fill-height
          >
            <div class="title">
              Gender:
            </div>
          </VLayout>
        </VFlex>
        <VFlex
          xs4
          sm5
          text-xs-center
        >
          <VBtn
            icon
            @click.prevent="handleGenderChanged('male')"
          >
            <VIcon
              x-large
              :color="maleIconColor"
            >
              mdi-human-male
            </VIcon>
          </VBtn>
        </VFlex>
        <VFlex
          xs4
          sm5
          text-xs-center
        >
          <VBtn
            icon
            @click.prevent="handleGenderChanged('female')"
          >
            <VIcon
              x-large
              :color="femaleIconColor"
            >
              mdi-human-female
            </VIcon>
          </VBtn>
        </VFlex>
        <VFlex xs6>
          <VTextField
            v-model="firstName"
            prepend-icon="perm_identity"
            placeholder="First Name"
          />
        </VFlex>
        <VFlex xs6>
          <VTextField
            v-model="lastName"
            placeholder="Last Name"
          />
        </VFlex>
        <VFlex xs6>
          <VTextField
            v-model="birthDate"
            prepend-icon="cake"
            mask="##/##"
            placeholder="Birth Date (ex: 01/02)"
          />
        </VFlex>
        <VFlex xs6>
          <VTextField
            v-model="birthyear"
            mask="####"
            placeholder="Birth Year (ex: 1992)"
          />
        </VFlex>
        <VFlex xs12>
          <AsyncAutoComplete
            icon="bookmark"
            label="categories"
            multiple
            :default-array-select="profileCategories"
            :get-gql-query="categoryGqlQuery"
            :default-items="categories"
            :get-query-variables="getCategoryVariables"
            :parse-data="parseCategoryData"
            :set-query-status="setCategoryQueryStatus"
            :get-query-status="getCategoryQueryStatus"
            :update-context-variable="updateCategoryContextVariable"
            :clear-all="clearCategories"
            @itemsChange="onCategoryChange"
          />
        </VFlex>
        <VFlex xs12>
          <AsyncAutoComplete
            icon="groups"
            label="groups"
            multiple
            :default-array-select="profileGroups"
            :get-gql-query="groupGqlQuery"
            :default-items="groups"
            :get-query-variables="getGroupVariables"
            :parse-data="parseGroupData"
            :set-query-status="setGroupQueryStatus"
            :get-query-status="getGroupQueryStatus"
            :update-context-variable="updateGroupContextVariable"
            :clear-all="clearGroups"
            @itemsChange="onGroupChange"
          />
        </VFlex>
        <VFlex xs6>
          <VTextField
            v-model="facebookUrl"
            prepend-icon="mdi-facebook"
            placeholder="Facebook"
            @click:prepend="openUrlTab(facebookUrl)"
          />
        </VFlex>
        <VFlex xs6>
          <VTextField
            v-model="instagramUrl"
            prepend-icon="mdi-instagram"
            placeholder="Instagram"
            @click:prepend="openUrlTab(instagramUrl)"
          />
        </VFlex>
        <VFlex xs6>
          <AsyncAutoComplete
            icon="business"
            label="organization"
            :default-select="profileOrganization"
            :get-gql-query="organizationGqlQuery"
            :default-items="organizations"
            :get-query-variables="getOrganizationVariables"
            :parse-data="parseOrgData"
            :set-query-status="setWorkHistoryQueryStatus"
            :get-query-status="getWorkHistoryQueryStatus"
            :update-context-variable="updateOrgContextVariable"
            :clear-all="clearOrgs"
            @itemsChange="onOrgChange"
          />
        </VFlex>
        <VFlex xs6>
          <AsyncAutoComplete
            icon="title"
            label="title"
            :default-select="profileTitle"
            :get-gql-query="titleGqlQuery"
            :default-items="titles"
            :get-query-variables="getTitleVariables"
            :parse-data="parseTitleData"
            :set-query-status="setWorkHistoryQueryStatus"
            :get-query-status="getWorkHistoryQueryStatus"
            :update-context-variable="updateTitleContextVariable"
            :clear-all="clearTitles"
            @itemsChange="onTitleChange"
          />
        </VFlex>
        <VFlex xs12>
          <VTextField
            v-model="description"
            outline
            prepend-icon="speaker_notes"
            label="Notes"
          />
        </VFlex>
      </VLayout>
    </VContainer>
  </VCard>
</template>
<script>
import { mapActions, mapGetters } from 'vuex'
import { categoryMapActions, groupMapActions, workHistoryMapActions } from '@/store/utils'
import AvatarUploader from './AvatarUploader'
import AsyncAutoComplete from './AsyncAutoComplete'
import { LIST_QUERY_CATEGORIES } from '@/backend/graphql/categoryGQL'
import { LIST_QUERY_GROUPS } from '@/backend/graphql/groupGQL'
import { LIST_QUERY_ORGANIZATIONS, LIST_QUERY_TITLES } from '../store/modules/workHistoryGQL'
import _ from 'lodash'
export default {
  name: 'EditContactForm',
  components: {
    AsyncAutoComplete,
    AvatarUploader
  },
  props: {
    dataBackgroundColor: {
      type: String,
      default: ''
    },
    totalCount: {
      type: Number,
      default: 0
    }
  },
  data () {
    return {
      categoryGqlQuery: LIST_QUERY_CATEGORIES,
      groupGqlQuery: LIST_QUERY_GROUPS,
      organizationGqlQuery: LIST_QUERY_ORGANIZATIONS,
      titleGqlQuery: LIST_QUERY_TITLES,
      limit: 30,
      offset: 0,
      orderBy: 'name',
      categoryName: '',
      groupName: '',
      orgOrderBy: 'organization__name',
      organizationName: '',
      organizationIsEmpty: false,
      organizationIsNull: false,
      titleOrderBy: 'title__name',
      titleName: '',
      titleIsEmpty: false,
      titleIsNull: false,
      avatarPlaceholder: require('@/assets/sndle-avatar-default.png'),
      isBirthDateEdited: false
    }
  },
  computed: {
    ...mapGetters({
      isDrawerVisible: 'messageDrawer/getVisibility',
      currentContact: 'contact/getCurrentContact',
      user: 'account/getUser'
    }),
    contactIndex () {
      return this.$store.state.contact.contactIndex
    },
    page: {
      get: function () {
        return this.contactIndex + 1
      },
      set: function (val) {
        if (val) {
          this.$store.dispatch('contact/setIndex', { val: val - 1 })
        }
      }
    },
    userId () {
      return String(_.get(this.user, 'id', -1))
    },
    number () {
      return this.$store.getters['contact/getNumberByIndex'](this.contactIndex)
    },
    contactId () {
      return this.$store.getters['contact/getIdByIndex'](this.contactIndex)
    },
    firstName: {
      get: function () {
        return this.$store.getters['contact/getFirstNameByIndex'](this.contactIndex)
      },
      set: function (val) {
        this.$store.dispatch('contact/setFirstNameByIndex', { val, index: this.contactIndex })
        this.$emit('changed')
      }
    },
    lastName: {
      get: function () {
        return this.$store.getters['contact/getLastNameByIndex'](this.contactIndex)
      },
      set: function (val) {
        this.$store.dispatch('contact/setLastNameByIndex', { val, index: this.contactIndex })
        this.$emit('changed')
      }
    },
    birthDate: {
      get: function () {
        return this.$store.getters['contact/getBirthDateByIndex'](this.contactIndex, this.isBirthDateEdited)
      },
      set: function (val) {
        this.$store.dispatch('contact/setBirthDateByIndex', { val, index: this.contactIndex })
        this.isBirthDateEdited = true
        this.$emit('changed')
      }
    },
    birthyear: {
      get: function () {
        return this.$store.getters['contact/getBirthYearByIndex'](this.contactIndex)
      },
      set: function (val) {
        this.$store.dispatch('contact/setBirthYearByIndex', { val, index: this.contactIndex })
        this.$emit('changed')
      }
    },
    facebookUrl: {
      get: function () {
        return this.$store.getters['contact/getFacebookUrlByIndex'](this.contactIndex)
      },
      set: function (val) {
        this.$store.dispatch('contact/setFacebookUrlByIndex', { val, index: this.contactIndex })
        this.$emit('changed')
      }
    },
    instagramUrl: {
      get: function () {
        return this.$store.getters['contact/getInstagramUrlByIndex'](this.contactIndex)
      },
      set: function (val) {
        this.$store.dispatch('contact/setInstagramUrlByIndex', { val, index: this.contactIndex })
        this.$emit('changed')
      }
    },
    avatarImg: {
      get: function () {
        return this.$store.getters['contact/getAvatarImgByIndex'](this.contactIndex)
      },
      set: function (val) {
        this.$store.dispatch('contact/setAvatarImgByIndex', { val, index: this.contactIndex })
        this.$emit('changed')
      }
    },
    gender: {
      get: function () {
        return this.$store.getters['contact/getGenderByIndex'](this.contactIndex)
      },
      set: function (val) {
        this.$store.dispatch('contact/setGenderByIndex', { val, index: this.contactIndex })
        this.$emit('changed')
      }
    },
    description: {
      get: function () {
        return this.$store.getters['contact/getDescriptionByIndex'](this.contactIndex)
      },
      set: function (val) {
        this.$store.dispatch('contact/setDescriptionByIndex', { val, index: this.contactIndex })
        this.$emit('changed')
      }
    },
    maleIconColor () {
      return this.gender === 1 ? 'blue darken-3' : 'disabled'
    },
    femaleIconColor () {
      return this.gender === 2 ? 'pink darken-2' : 'disabled'
    },
    profileCategories () {
      return this.$store.getters['contact/getCategoriesByIndex'](this.contactIndex).map((item) => {
        return item.name
      })
    },
    profileGroups () {
      return this.$store.getters['contact/getGroupsByIndex'](this.contactIndex).map((item) => {
        return item.name
      })
    },
    profileOrganization () {
      return this.$store.getters['contact/getOrganizationByIndex'](this.contactIndex)
    },
    profileTitle () {
      return this.$store.getters['contact/getTitleByIndex'](this.contactIndex)
    },
    categories: {
      get: function () {
        return this.$store.getters['category/getAllCategories'].map(item => {
          return item.name
        })
      },
      set: function (val) {
        val = val.map(item => { return _.startCase(item.trim()) })
        this.$store.dispatch('contact/setCategoryByIndex', { val, index: this.contactIndex })
        this.$emit('changed')
      }
    },
    groups: {
      get: function () {
        return this.$store.getters['group/getAllGroups'].map(item => {
          return item.name
        })
      },
      set: function (val) {
        val = val.map(item => { return _.startCase(item.trim()) })
        this.$store.dispatch('contact/setGroupByIndex', { val, index: this.contactIndex })
        this.$emit('changed')
      }
    },
    organizations: {
      get: function () {
        return this.$store.getters['workHistory/getAllOrganizations'].map(item => {
          return item.name
        })
      },
      set: function (val) {
        if (!val) val = ''
        val = _.startCase(val.trim())
        this.$store.dispatch('contact/setOrganizationByIndex', { val, index: this.contactIndex })
        this.$emit('changed')
      }
    },
    titles: {
      get: function () {
        return this.$store.getters['workHistory/getAllTitles'].map(item => {
          return item.name
        })
      },
      set: function (val) {
        if (!val) val = ''
        val = _.startCase(val.trim())
        this.$store.dispatch('contact/setTitleByIndex', { val, index: this.contactIndex })
        this.$emit('changed')
      }
    },
    metLocations: {
      get: function () {
        return this.$store.getters['metLocation/getAllLocations'].map(item => {
          return item.name
        })
      },
      set: function (val) {
        val = val.map(item => { return _.startCase(item.trim()) })
        this.$store.dispatch('contact/setMetLocationByIndex', { val, index: this.contactIndex })
        this.$emit('changed')
      }
    }
  },
  watch: {
    contactIndex () {
      // use debounce
      this.debouncedContactIndexChange()
    }
  },
  created: function () {
    this.debouncedContactIndexChange = _.debounce(this.onContactIndexChanged, 300)
  },
  methods: {
    ...mapActions(Object.assign({}, categoryMapActions, groupMapActions, workHistoryMapActions)),
    onContactIndexChanged () {
      const contact = this.$store.getters['contact/getCurrentContact'](this.contactIndex)
      this.$store.dispatch('messageDrawer/setItem', { val: contact })
    },
    getCategoryQueryStatus () {
      return this.$store.getters['category/getLoadingStatus']
    },
    setCategoryQueryStatus (val) {
      this.$store.dispatch('category/setLoadingStatus', { val })
    },
    getGroupQueryStatus () {
      return this.$store.getters['group/getLoadingStatus']
    },
    setGroupQueryStatus (val) {
      this.$store.dispatch('group/setLoadingStatus', { val })
    },
    getWorkHistoryQueryStatus () {
      return this.$store.getters['workHistory/getLoadingStatus']
    },
    setWorkHistoryQueryStatus (val) {
      this.$store.dispatch('workHistory/setLoadingStatus', { val })
    },
    resetInitialValue (val) {
      this.$refs.avatarUploader.setIsResetNeeded(true)
      this.isBirthDateEdited = val
    },
    onSliderStarted (val) {
      this.$emit('sliderStart', val)
    },
    onSliderChanged (val) {
      this.$emit('sliderChange', val)
    },
    openUrlTab (val) {
      if (val) window.open(val)
    },
    updateCategoryContextVariable (key, value) {
      if (key === 'name') key = 'categoryName'
      if (this.hasOwnProperty(key)) {
        this[key] = value
      }
    },
    updateGroupContextVariable (key, value) {
      if (key === 'name') key = 'groupName'
      if (this.hasOwnProperty(key)) {
        this[key] = value
      }
    },
    updateOrgContextVariable (key, value) {
      if (key === 'name') key = 'organizationName'
      if (this.hasOwnProperty(key)) {
        this[key] = value
      }
    },
    updateTitleContextVariable (key, value) {
      if (key === 'name') key = 'titleName'
      if (this.hasOwnProperty(key)) {
        this[key] = value
      }
    },
    handleGenderChanged (val) {
      switch (val) {
        case 'male':
          this.gender = this.gender === 1 ? 0 : 1
          break
        case 'female':
          this.gender = this.gender === 2 ? 0 : 2
          break
        default:
          break
      }
    },
    getCategoryVariables () {
      return {
        limit: this.limit,
        offset: this.offset,
        orderBy: this.orderBy,
        name: this.categoryName
      }
    },
    getCategoryItems (data) { return _.get(data, 'listCategories.results', {}) },
    getTotalCategoryCount (data) { return _.get(data, 'listCategories.totalCount', 0) },
    parseCategoryData (data) {
      if (this.categories.length !== 0) {
        this.clearCategories()
      }
      this.initCategories({
        newCategories: this.getCategoryItems(data),
        totalCount: this.getTotalCategoryCount(data),
        isFullArraySize: false
      })
    },
    onCategoryChange (value) {
      this.categories = value
    },
    getGroupVariables () {
      return {
        limit: this.limit,
        offset: this.offset,
        orderBy: this.orderBy,
        name: this.groupName
      }
    },
    getGroupItems (data) { return _.get(data, 'listGroups.results', {}) },
    getTotalGroupCount (data) { return _.get(data, 'listGroups.totalCount', 0) },
    parseGroupData (data) {
      if (this.groups.length === 0) {
        this.initGroups({
          newGroups: this.getGroupItems(data),
          totalCount: this.getTotalGroupCount(data),
          isFullArraySize: false
        })
      } else {
        this.clearGroups()
        this.initGroups({
          newGroups: this.getGroupItems(data),
          totalCount: this.getTotalGroupCount(data),
          isFullArraySize: false
        })
      }
    },
    onGroupChange (value) {
      this.groups = value
    },
    getOrganizationVariables () {
      return {
        limit: this.limit,
        offset: this.offset,
        orgOrderBy: this.orgOrderBy,
        organizationName: this.organizationName,
        organizationIsEmpty: this.organizationIsEmpty,
        organizationIsNull: this.organizationIsNull,
        userId: this.userId
      }
    },
    getOrganizationItems (data) {
      const listWorkHistory = _.get(data, 'listWorkHistory.results', [])
      return listWorkHistory.map((item) => {
        return item.organization
      })
    },
    getTotalOrgCount (data) { return _.get(data, 'listWorkHistory.totalCount', 0) },
    parseOrgData (data) {
      if (this.organizations.length === 0) {
        this.initOrgs({
          newOrgs: this.getOrganizationItems(data),
          totalCount: this.getTotalOrgCount(data),
          isFullArraySize: false
        })
      } else {
        this.clearOrgs()
        this.initOrgs({
          newOrgs: this.getOrganizationItems(data),
          totalCount: this.getTotalOrgCount(data),
          isFullArraySize: false
        })
      }
    },
    onOrgChange (value) {
      this.organizations = value
    },
    getTitleVariables () {
      return {
        limit: this.limit,
        offset: this.offset,
        titleOrderBy: this.titleOrderBy,
        titleName: this.titleName,
        titleIsEmpty: this.titleIsEmpty,
        titleIsNull: this.titleIsNull,
        userId: this.userId
      }
    },
    getTitleItems (data) {
      const listWorkHistory = _.get(data, 'listWorkHistory.results', [])
      return listWorkHistory.map((item) => {
        return item.title
      })
    },
    getTotalTitleCount (data) { return _.get(data, 'listWorkHistory.totalCount', 0) },
    parseTitleData (data) {
      if (this.titles.length === 0) {
        this.initTitles({
          newTitles: this.getTitleItems(data),
          totalCount: this.getTotalTitleCount(data),
          isFullArraySize: false
        })
      } else {
        this.clearTitles()
        this.initTitles({
          newTitles: this.getTitleItems(data),
          totalCount: this.getTotalTitleCount(data),
          isFullArraySize: false
        })
      }
    },
    onTitleChange (value) {
      this.titles = value
    },
    onSaveContact () {
      const contactData = Object.assign({}, this.currentContact(this.contactIndex))
      this.$store.dispatch('contact/updateContact', { contactData, index: this.contactIndex })
    },
    onMessageClicked () {
      if (!this.isDrawerVisible) {
        this.$store.dispatch('messageDrawer/setVisibility', { val: true })
        this.$store.dispatch('messageDrawer/setItem', { val: this.currentContact(this.contactIndex), forced: true })
      }
    }
  }
}

</script>
<style scoped>
  .sub-title {
    font-size: 16px;
  }
</style>
